/**
 * Template Merger Utility
 * Merges base template JSON with user form data
 */

import API_BASE_URL from '../config/api';

/**
 * Deep merge utility function
 * Recursively merges source into target, preserving nested structures
 * @param {Object} target - Base object to merge into
 * @param {Object} source - Object with values to merge
 * @returns {Object} Merged object
 */
const deepMerge = (target, source) => {
  if (!source || typeof source !== 'object') {
    return target;
  }
  if (!target || typeof target !== 'object') {
    return source;
  }

  const result = { ...target };

  for (const key in source) {
    if (source.hasOwnProperty(key)) {
      const sourceValue = source[key];
      const targetValue = target[key];

      // If both values are objects (not arrays), recursively merge
      if (
        sourceValue && typeof sourceValue === 'object' && !Array.isArray(sourceValue) &&
        targetValue && typeof targetValue === 'object' && !Array.isArray(targetValue)
      ) {
        result[key] = deepMerge(targetValue, sourceValue);
      } else if (sourceValue !== undefined && sourceValue !== null && sourceValue !== '') {
        // Only override if source value is meaningful (not empty)
        result[key] = sourceValue;
      }
    }
  }

  return result;
};

// Base template JSON files mapping
const TEMPLATE_BASES = {
  1: `/static/template_bases/template_1.json`,
  2: `/static/template_bases/template_2.json`,
  3: `/static/template_bases/template_3.json`,
  4: `/static/template_bases/template_4.json`,
  5: `/static/template_bases/template_5.json`,
  6: `/static/template_bases/template_6.json`,
  7: `/static/template_bases/template_7.json`,
  8: `/static/template_bases/template_8.json`,
  9: `/static/template_bases/template_9.json`,
  10: `/static/template_bases/template_10.json`,
  11: `/static/template_bases/template_11.json`,
  12: `/static/template_bases/template_12.json`,
  13: `/static/template_bases/template_13.json`,
  14: `/static/template_bases/template_14.json`,
  15: `/static/template_bases/template_15.json`,
  16: `/static/template_bases/template_16.json`,
  17: `/static/template_bases/template_17.json`,
  18: `/static/template_bases/template_18.json`,
  19: `/static/template_bases/template_19.json`,
  20: `/static/template_bases/template_20.json`
};

/**
 * Load base template JSON from server
 * @param {number} templateId - Template ID (1-20)
 * @returns {Promise<Object>} Base template JSON
 */
export const loadBaseTemplate = async (templateId) => {
  try {
    const templatePath = TEMPLATE_BASES[templateId];

    if (!templatePath) {
      throw new Error(`Template ${templateId} base JSON not found. Please create it first.`);
    }

    const url = `${API_BASE_URL}${templatePath}`;

    const response = await fetch(url);

    if (!response.ok) {
      throw new Error(`Failed to load template ${templateId}: ${response.status} ${response.statusText}`);
    }

    const baseTemplate = await response.json();
    return baseTemplate;
  } catch (error) {
    throw new Error(`Failed to fetch template: ${error.message}`);
  }
};

/**
 * Merge base template with user form data
 * Properly deep merges theme to preserve all base template structure
 * @param {Object} baseTemplate - Base template JSON from file
 * @param {Object} formData - User's form data
 * @returns {Object} Complete merged JSON
 */
export const mergeTemplateWithFormData = (baseTemplate, formData) => {
  try {
    // Create a deep copy of base template
    const mergedTemplate = JSON.parse(JSON.stringify(baseTemplate));

    // Merge theme - preserve ALL base template theme properties
    if (formData.theme) {
      const baseTheme = mergedTemplate.theme || {};
      const userTheme = formData.theme || {};

      // Build user colors object from form data
      // Map simple user values to base template structure
      const userColors = {};
      if (userTheme.colors) {
        // Primary color - can be string or array in base template
        if (userTheme.colors.primary) {
          // If base template has array, update first element, else use as string
          if (Array.isArray(baseTheme.colors?.primary)) {
            userColors.primary = [userTheme.colors.primary, userTheme.colors.primary];
          } else {
            userColors.primary = userTheme.colors.primary;
          }
        }

        // Secondary color
        if (userTheme.colors.secondary) {
          userColors.secondary = userTheme.colors.secondary;
        }

        // Accent color (may not exist in all base templates)
        if (userTheme.colors.accent) {
          userColors.accent = userTheme.colors.accent;
        }

        // Text color - base template may have nested text object
        if (userTheme.colors.text) {
          // If base has text as object, update primary text
          if (baseTheme.colors?.text && typeof baseTheme.colors.text === 'object') {
            userColors.text = {
              ...baseTheme.colors.text,
              primary: userTheme.colors.text
            };
          } else {
            userColors.text = userTheme.colors.text;
          }
        }

        // Background color
        if (userTheme.colors.background) {
          userColors.background = userTheme.colors.background;
        }
      }

      // Build user typography from fonts
      const userTypography = {};
      if (userTheme.fonts) {
        if (userTheme.fonts.heading) {
          userTypography.heading = userTheme.fonts.heading;
        }
        if (userTheme.fonts.body) {
          userTypography.body = userTheme.fonts.body;
        }
      }

      // Deep merge: base theme + user colors + user typography
      // This preserves gray scales, surface, border, text variants, sizes, weights
      mergedTemplate.theme = {
        ...baseTheme,
        colors: deepMerge(baseTheme.colors || {}, userColors),
        typography: deepMerge(baseTheme.typography || {}, userTypography)
      };

      // Preserve spacing from base template
      if (baseTheme.spacing) {
        mergedTemplate.theme.spacing = baseTheme.spacing;
      }

    }

    // Deep merge content section with user's data
    if (formData.content) {
      mergedTemplate.content = deepMerge(mergedTemplate.content || {}, formData.content);
    }

    return mergedTemplate;
  } catch (error) {
    throw error;
  }
};

/**
 * Generate complete JSON for publishing
 * @param {number} templateId - Template ID (1-20)
 * @param {Object} formData - User's form data
 * @returns {Promise<Object>} Complete JSON ready for publishing
 */
export const generateCompleteJSON = async (templateId, formData) => {
  try {
    // Step 1: Load base template
    const baseTemplate = await loadBaseTemplate(templateId);

    // Step 2: Merge with user data
    const completeJSON = mergeTemplateWithFormData(baseTemplate, formData);

    // Step 3: Validate required fields
    validateCompleteJSON(completeJSON);

    return completeJSON;
  } catch (error) {
    throw error;
  }
};

/**
 * Validate that JSON has all required fields
 * @param {Object} json - JSON to validate
 * @throws {Error} If validation fails
 */
export const validateCompleteJSON = (json) => {
  const requiredFields = ['version', 'page_type', 'template_id', 'template_name', 'blocks', 'content'];

  for (const field of requiredFields) {
    if (!(field in json)) {
      throw new Error(`Missing required field: ${field}`);
    }
  }

  // Validate version
  if (json.version !== '2.0') {
    throw new Error('Invalid JSON version. Must be "2.0"');
  }

  // Validate blocks is array
  if (!Array.isArray(json.blocks)) {
    throw new Error('blocks must be an array');
  }

  // Validate content is object
  if (typeof json.content !== 'object' || json.content === null) {
    throw new Error('content must be an object');
  }

  return true;
};

/**
 * Check if template base exists
 * @param {number} templateId - Template ID to check
 * @returns {boolean} True if base exists
 */
export const templateBaseExists = (templateId) => {
  return templateId in TEMPLATE_BASES;
};

export default {
  loadBaseTemplate,
  mergeTemplateWithFormData,
  generateCompleteJSON,
  validateCompleteJSON,
  templateBaseExists
};
